import * as STD from '@emersya/mpa-std';
import * as CM from '@emersya/mpa-components-manager';

export interface IMenuAccordionComponentConfiguration extends IMenuViewsComponentConfiguration {
};

export class MenuAccordionComponent extends MenuViewsComponent<
	MenuAccordionComponent,
	IMenuAccordionComponentConfiguration
> {
	override async open(childrenIds: string | string[]) {
		childrenIds = Array.isArray(childrenIds) ? childrenIds : [childrenIds];

		const closingChildrenIds = this.currentSections
			.filter(section => !childrenIds.includes(section.id))
			.map(section => section.id);

		if (closingChildrenIds.length > 0) {
			const closable = await this.close(closingChildrenIds);
			if (!closable) return false;
		}

		const proceed = await super.open(childrenIds);
		if (!proceed) return false;

		const alreadyOpenChildrenIds = this.currentSections.map(section => section.id);
		for (const childId of childrenIds) {
			if (alreadyOpenChildrenIds.includes(childId)) continue;

			const child = <
				SectionComponent | undefined
				>this.component_findChild(childId);

			if (!child) {
				this.component_throwUserError(`Child<${childId}> not found!`, {
					childId,
					component: this
				});
			}

			this.currentSections.push(child);
			child.component_DOM.classList.add('opened');
		}

		await this.component_dispatchVisibilityUpdateOnChilds();

		for (const child of this.currentSections) {
			await child.component_dispatchVisibilityUpdateOnChilds();
		}

		for (const child of this.component_outerChilds) {
			if (child instanceof CM.DOMComponent) {
				await child.component_dispatchVisibilityUpdateOnChilds();
			}
		}

		return true;
	};

	override async close(childrenIds: string | string[]) {
		childrenIds = Array.isArray(childrenIds) ? childrenIds : [childrenIds];

		if (childrenIds.length === 0 || this.currentSections.length === 0) {
			return false;
		}

		const proceed = await super.close(childrenIds);
		if (!proceed) return false;

		const closedSections: SectionComponent[] = [];

		this.currentSections = this.currentSections
			.filter(section => {
				if (childrenIds.includes(section.id)) {
					closedSections.push(section);
					section.component_DOM.classList.remove('opened');
					return false;
				}
				return true;
			});

		await this.component_dispatchVisibilityUpdateOnChilds();

		for (const child of closedSections) {
			await child.component_dispatchVisibilityUpdateOnChilds();
		}

		for (const child of this.component_innerChilds) {
			if (child instanceof CM.DOMComponent) {
				await child.component_dispatchVisibilityUpdateOnChilds();
			}
		}

		return true;
	};

	private async onInputButton(event: STD.events.Event<any, CM.Component<any, any>>) {
		if (!(event.target instanceof CM.Component)) {
			throw STD.errors.user('Event target is not a component!', {event});
		}
		if (
			!event.target.component_parent ||
			(event.target.component_parent.component_parent !== this) ||
			(event.target.id !== 'header')
		) {
			return;
		}

		const section = <SectionComponent>event.target.component_parent;

		if (!this.currentSections.includes(section)) {
			await this.open(section.id);
		} else {
			await this.close(section.id);
		}
	};

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: IMenuAccordionComponentConfiguration
	) {
		super(descriptor, configuration);
		this.component_DOM = <HTMLDivElement>STD.DOM.factory({});
		this.addEventListener('value', this.onInputButton.bind(this), {
			subType: 'input-button',
			listener: this,
			disableEventExistsCheck: true
		});
	};
};
export namespace NS {
	export const Component = MenuAccordionComponent;
	export type Component = MenuAccordionComponent;
	export type Configuration = IMenuAccordionComponentConfiguration;
}
;
import './MenuAccordion.scss';
import {MenuViewsComponent, IMenuViewsComponentConfiguration} from '../MenuViews/MenuViews';
import {SectionComponent} from '../../Section/Section';

CM.Manager.declare({
	id: 'menu-accordion',
	type: MenuAccordionComponent
});
